import {
  createContext,
  createElement,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
  FREE_TRIAL_FEATURES,
  FREE_TRIAL_POST_LIMIT,
  FREE_TRIAL_PROGRAM_LIMIT,
  FREE_TRIAL_TOTAL_LIMIT,
  isPartnerProPlus,
} from "@/data/partnerBillingPlans";
// import { dispatchIntegrationEvent } from "@/lib/partnerIntegrations"; // Resend + webhooks paused

export type PartnerFollower = {
  student_id: string;
  display_name: string;
  profile_image_url?: string | null;
  grade?: string | null;
  country?: string | null;
  school?: string | null;
  followed_at: string;
};

export type PostReactor = {
  user_id: string;
  display_name: string;
  profile_image_url?: string | null;
  reaction?: string;
};

export type PostReply = {
  id: string;
  story_id: string;
  author_user_id: string;
  body: string;
  created_at: string;
  display_name: string;
  profile_image_url?: string | null;
  grade?: string | null;
  parent_reply_id?: string | null;
  is_partner?: boolean;
  cheers_count?: number;
  has_cheered?: boolean;
};

export type PartnerPost = {
  id: string;
  author_user_id: string;
  partner_id?: string;
  name?: string;
  title?: string;
  body: string;
  media_image_url?: string | null;
  tags?: string[];
  audience_type?: "highSchool" | "afterHighSchool" | "all";
  is_verified_badge?: boolean;
  is_published?: boolean;
  is_approved?: boolean;
  moderation_status?: string;
  cheers_count?: number;
  reply_count?: number;
  reactors_preview?: PostReactor[];
  created_at: string;
  updated_at?: string;
};

export type PartnerProfile = {
  id: string;
  institution_name?: string;
  institution_type?: string;
  email?: string;
  logo_url?: string;
  avatar_url?: string;
  banner_url?: string;
  created_at?: string;
  description?: string;
  website?: string;
  mission?: string;
  history?: string;
  locations?: string[] | string;
  country?: string;
  phone?: string;
  is_verified?: boolean;
  profile_completed_at?: string | null;
  verified_at?: string | null;
};

export type PartnerProgram = {
  id: string;
  title: string;
  description?: string;
  location?: string;
  funding_type?: string;
  is_active?: boolean;
  created_at: string;
  category?: string;
  application_deadline?: string;
  type?: string;
  image_url?: string | null;
};

export type PartnerAnalytics = {
  program_id: string;
  total_views: number;
  total_applications: number;
  total_bookmarks: number;
};

export type PartnerApplication = {
  id: string;
  student_id: string;
  program_id: string;
  status: string;
  rating?: number;
  applied_at: string;
  cover_letter?: string;
  application_responses?: Record<string, unknown> | null;
  additional_notes?: string | null;
  rejection_reason?: string | null;
  submitted_documents?: Array<{
    id?: string;
    name?: string;
    category?: string;
    sub_category?: string;
    bucket?: string;
    remote_path?: string;
  }>;
  student_name?: string;
  student_email?: string;
  student_school?: string;
  student_grade?: string;
  student_avatar_url?: string;
  program_title?: string;
  country?: string;
};

export type PartnerActivity = {
  id: string;
  action_type: string;
  description: string;
  related_program_id?: string;
  related_user_name?: string;
  created_at: string;
};

export type PartnershipMessage = {
  id: string;
  sender_id: string;
  receiver_id?: string;
  partner_id?: string;
  application_id?: string;
  subject?: string;
  content: string;
  message_type: string;
  is_from_system: boolean;
  is_read: boolean;
  sent_at: string;
  parent_message_id?: string;
  attachment_url?: string | null;
  attachment_name?: string | null;
  attachment_mime_type?: string | null;
  attachment_size?: number | null;
  deleted_at?: string | null;
  deleted_by?: string | null;
};

export type SubscriptionPlan = {
  id: string;
  name: string;
  description?: string;
  price_monthly: number;
  price_yearly?: number;
  currency: string;
  features: any;
  max_campaigns: number;
  max_duration_days: number;
  featured_placement: boolean;
  analytics_access: boolean;
  priority_support: boolean;
  lead_insights: boolean;
  higher_search_ranking: boolean;
  is_active: boolean;
};

export type Subscription = {
  id: string;
  partner_id: string;
  plan_id: string;
  status: string;
  start_date: string;
  end_date?: string;
  trial_end_date?: string;
  auto_renew: boolean;
  plan?: SubscriptionPlan;
};

export type Campaign = {
  id: string;
  partner_id: string;
  subscription_id?: string;
  name: string;
  description?: string;
  target_regions: string[];
  daily_budget?: number;
  total_budget?: number;
  status: string;
  start_date: string;
  end_date: string;
  days_used: number;
  progress_percentage: number;
};

export type ChatPeerLabel = {
  student_id: string;
  student_name: string;
  student_email?: string;
  avatar_url?: string;
};

type PartnerDataValue = {
  profile: PartnerProfile | null;
  programs: PartnerProgram[];
  posts: PartnerPost[];
  analytics: PartnerAnalytics[];
  applications: PartnerApplication[];
  followers: PartnerFollower[];
  activities: PartnerActivity[];
  messages: PartnershipMessage[];
  chatPeers: Record<string, ChatPeerLabel>;
  subscription: Subscription | null;
  campaign: Campaign | null;
  subscriptionPlans: SubscriptionPlan[];
  platformLearnerCount: number;
  loading: boolean;
  activeCount: number;
  refresh: () => Promise<void>;
  markPartnershipThreadReadLocal: (
    partnerId: string,
    studentId: string,
    subject?: string | null
  ) => void;
  deleteProgram: (programId: string) => Promise<{ error: any }>;
  setProgramActive: (programId: string, isActive: boolean) => Promise<{ error: any }>;
  createPost: (payload: {
    title?: string;
    body: string;
    media_image_url?: string | null;
    tags?: string[];
    audience_type?: "highSchool" | "afterHighSchool" | "all";
    is_published?: boolean;
  }) => Promise<PartnerPost>;
  updatePost: (
    id: string,
    payload: {
      title?: string;
      body?: string;
      media_image_url?: string | null;
      tags?: string[];
      audience_type?: "highSchool" | "afterHighSchool" | "all";
      is_published?: boolean;
    }
  ) => Promise<PartnerPost>;
  togglePostPublish: (id: string, isPublished: boolean) => Promise<void>;
  deletePost: (id: string) => Promise<void>;
  fetchPostReactors: (storyId: string) => Promise<PostReactor[]>;
  fetchPostReplies: (storyId: string) => Promise<PostReply[]>;
  addPostReply: (storyId: string, body: string, parentReplyId?: string) => Promise<void | PostReply>;
  deletePostReply: (replyId: string) => Promise<void>;
  toggleReplyCheer: (replyId: string) => Promise<void>;
  updateApplicationStatus: (
    applicationId: string,
    status: string,
    rejectionReason?: string
  ) => Promise<{ error: any }>;
  deleteApplication: (applicationId: string) => Promise<{ error: any }>;
  deletePartnershipMessage: (messageId: string) => Promise<{ error: any }>;
  deletePartnershipThread: (
    studentId: string,
    subject?: string | null
  ) => Promise<{ error: any }>;
  sendApplicationMessage: (opts: {
    studentId: string;
    applicationId: string;
    subject: string;
    content: string;
  }) => Promise<{ error: any }>;
  sendFollowerMessage: (opts: {
    studentId: string;
    content: string;
    subject?: string;
  }) => Promise<{ error: any; messageId?: string | null }>;
  isProPlus: boolean;
  maxProgramListings: number;
  maxPostListings: number;
  totalListingCount: number;
  maxTotalListings: number;
  updateProfile: () => Promise<void>;
  saveInstitutionName: (institutionName: string) => Promise<{ error: Error | null }>;
  user: ReturnType<typeof useAuth>["user"];
};

const normalizePartnershipSubject = (subject?: string | null) =>
  (subject || "No Subject").replace(/^Re:\s*/gi, "").trim() ||
  "Application message";

const partnershipThreadMatches = (
  msg: PartnershipMessage,
  partnerId: string,
  studentId: string,
  subject?: string | null
) => {
  const norm = normalizePartnershipSubject(subject);
  const msgNorm = normalizePartnershipSubject(msg.subject);
  const msgPartnerId = msg.partner_id || partnerId;
  const msgStudentId =
    msg.sender_id === partnerId ? msg.receiver_id : msg.sender_id;
  return (
    msgPartnerId === partnerId &&
    msgStudentId === studentId &&
    msgNorm === norm
  );
};

const PartnerDataContext = createContext<PartnerDataValue | null>(null);

export function PartnerDataProvider({ children }: { children: ReactNode }) {
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState<PartnerProfile | null>(null);
  const [programs, setPrograms] = useState<PartnerProgram[]>([]);
  const [posts, setPosts] = useState<PartnerPost[]>([]);
  const [analytics, setAnalytics] = useState<PartnerAnalytics[]>([]);
  const [applications, setApplications] = useState<PartnerApplication[]>([]);
  const [followers, setFollowers] = useState<PartnerFollower[]>([]);
  const [activities, setActivities] = useState<PartnerActivity[]>([]);
  const [messages, setMessages] = useState<PartnershipMessage[]>([]);
  const [chatPeers, setChatPeers] = useState<Record<string, ChatPeerLabel>>({});
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [subscriptionPlans, setSubscriptionPlans] = useState<SubscriptionPlan[]>([]);
  const [platformLearnerCount, setPlatformLearnerCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);

  const fetchData = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (!user?.id) {
        setLoading(false);
        return;
      }

      // Keep existing UI visible on subsequent refreshes / navigations
      if (!opts?.silent && !hasLoadedOnce.current) {
        setLoading(true);
      }

      try {
        const { data: partnerData, error: partnerError } = await supabase
          .from("partners")
          .select("*")
          .eq("id", user.id)
          .maybeSingle();

        if (partnerError) {
          console.error("Error fetching partner data:", partnerError);
          setProfile(null);
        } else {
          setProfile(partnerData);
        }

        const { data: plansData, error: plansError } = await supabase
          .from("subscription_plans")
          .select("*")
          .eq("is_active", true);

        if (plansError) {
          console.error("Error fetching subscription plans:", plansError);
          setSubscriptionPlans([]);
        } else {
          setSubscriptionPlans(plansData || []);
        }

        if (partnerData) {
          const { data: programsData, error: programsError } = await supabase
            .from("programs")
            .select("*")
            .eq("institution_id", user.id)
            .order("created_at", { ascending: false });

          if (programsError) {
            console.error("Error fetching programs:", programsError);
            setPrograms([]);
          } else {
            setPrograms(programsData || []);
          }

          const programIds = (programsData || []).map((p) => p.id);

          const { data: analyticsData, error: analyticsError } = await supabase
            .from("program_analytics")
            .select("*")
            .in("program_id", programIds.length ? programIds : ["__none__"]);

          if (analyticsError) {
            console.error("Error fetching analytics:", analyticsError);
            setAnalytics([]);
          } else {
            setAnalytics(analyticsData || []);
          }

          const { data: applicationsData, error: applicationsError } = await supabase
            .from("applications")
            .select("*")
            .eq("partner_id", user.id)
            .order("applied_at", { ascending: false });

          if (applicationsError) {
            console.error("Error fetching applications:", applicationsError);
            setApplications([]);
          } else {
            const apps = applicationsData || [];
            const studentIds = [
              ...new Set(
                apps
                  .map((app: any) => app.student_id)
                  .filter((id: unknown): id is string => typeof id === "string" && id.length > 0)
              ),
            ];

            let labelById = new Map<
              string,
              {
                student_name?: string;
                student_email?: string;
                country?: string;
                school?: string;
                grade?: string;
                avatar_url?: string;
              }
            >();

            if (studentIds.length > 0) {
              const { data: labels, error: labelsError } = await supabase.rpc(
                "get_partner_applicant_labels",
                { p_student_ids: studentIds }
              );
              if (labelsError) {
                console.error("Error fetching applicant labels:", labelsError);
              } else {
                labelById = new Map(
                  (labels || []).map((row: any) => [
                    row.student_id,
                    {
                      student_name: row.student_name,
                      student_email: row.student_email,
                      country: row.country,
                      school: row.school,
                      grade: row.grade,
                      avatar_url: row.avatar_url,
                    },
                  ])
                );
              }
            }

            setApplications(
              apps.map((app: any) => {
                const label = labelById.get(app.student_id);
                return {
                  ...app,
                  status: app.status === "pending" ? "new" : app.status,
                  submitted_documents: Array.isArray(app.submitted_documents)
                    ? app.submitted_documents
                    : [],
                  student_name:
                    app.student_display_name ||
                    label?.student_name ||
                    "Student",
                  student_email:
                    app.student_email ||
                    label?.student_email ||
                    "",
                  country:
                    app.student_country ||
                    label?.country ||
                    "",
                  student_school:
                    app.student_school ||
                    label?.school ||
                    "",
                  student_grade:
                    app.student_grade ||
                    label?.grade ||
                    "",
                  student_avatar_url: label?.avatar_url || "",
                  program_title:
                    programsData?.find((p: any) => p.id === app.program_id)
                      ?.title || "Unknown Program",
                };
              })
            );
          }

          const { data: learnerCount, error: learnerCountError } = await supabase.rpc(
            "get_platform_learner_count"
          );
          if (learnerCountError) {
            console.error("Error fetching platform learner count:", learnerCountError);
            setPlatformLearnerCount(0);
          } else {
            setPlatformLearnerCount(Number(learnerCount) || 0);
          }

          const { data: followersData, error: followersError } = await supabase.rpc(
            "list_partner_followers"
          );
          if (followersError) {
            console.error("Error fetching followers:", followersError);
            setFollowers([]);
          } else {
            setFollowers((followersData as PartnerFollower[]) || []);
          }

          const { data: activityData, error: activityError } = await supabase
            .from("partner_activity")
            .select("*")
            .eq("partner_id", user.id)
            .order("created_at", { ascending: false })
            .limit(10);

          if (activityError) {
            console.error("Error fetching activities:", activityError);
            setActivities([]);
          } else {
            setActivities(activityData || []);
          }

          const { data: messagesData, error: messagesError } = await supabase
            .from("partnership_messages")
            .select("*")
            .or(`partner_id.eq.${user.id},receiver_id.eq.${user.id}`)
            .order("sent_at", { ascending: false })
            .limit(200);

          if (messagesError) {
            console.error("Error fetching messages:", messagesError);
            setMessages([]);
            setChatPeers({});
          } else {
            const rows = (messagesData || []) as PartnershipMessage[];
            setMessages(rows);

            const peerIds = Array.from(
              new Set(
                rows
                  .flatMap((m) => [m.sender_id, m.receiver_id])
                  .filter((id): id is string => !!id && id !== user.id)
              )
            );
            if (peerIds.length > 0) {
              const { data: peerLabels } = await supabase.rpc(
                "get_partner_applicant_labels",
                { p_student_ids: peerIds }
              );
              const map: Record<string, ChatPeerLabel> = {};
              for (const row of peerLabels || []) {
                map[row.student_id] = {
                  student_id: row.student_id,
                  student_name: row.student_name || "Student",
                  student_email: row.student_email || "",
                  avatar_url: row.avatar_url || "",
                };
              }
              setChatPeers(map);
            } else {
              setChatPeers({});
            }
          }

          const { data: subscriptionData, error: subscriptionError } = await supabase
            .from("subscriptions")
            .select("*, subscription_plans(*)")
            .eq("partner_id", user.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (subscriptionError) {
            console.error("Error fetching subscription:", subscriptionError);
            setSubscription(null);
          } else if (subscriptionData) {
            setSubscription({
              ...subscriptionData,
              plan: subscriptionData.subscription_plans,
            });
          }

          const { data: campaignData, error: campaignError } = await supabase
            .from("campaigns")
            .select("*")
            .eq("partner_id", user.id)
            .order("created_at", { ascending: false })
            .limit(1)
            .maybeSingle();

          if (campaignError) {
            console.error("Error fetching campaign:", campaignError);
            setCampaign(null);
          } else {
            setCampaign(campaignData);
          }

          const { data: postsData, error: postsError } = await supabase
            .from("student_stories")
            .select("*")
            .or(`partner_id.eq.${user.id},author_user_id.eq.${user.id}`)
            .order("created_at", { ascending: false });

          if (postsError) {
            console.error("Error fetching partner posts:", postsError);
            setPosts([]);
          } else if (postsData && postsData.length > 0) {
            const storyIds = postsData.map((p) => p.id);

            // Fetch live engagement metrics (cheers count, replies count, and reactors preview)
            const { data: engagementRows, error: engErr } = await supabase.rpc(
              "get_partner_posts_engagement",
              { p_story_ids: storyIds }
            );

            if (!engErr && engagementRows && Array.isArray(engagementRows)) {
              const engagementMap = new Map(
                engagementRows.map((row: any) => [row.post_id, row])
              );

              const enriched: PartnerPost[] = postsData.map((p) => {
                const eng = engagementMap.get(p.id);
                const realCheers = eng?.cheers_count ?? p.cheers_count ?? 0;
                const realReplies = eng?.reply_count ?? p.reply_count ?? 0;
                let preview = eng?.reactors_preview || [];
                if (typeof preview === "string") {
                  try { preview = JSON.parse(preview); } catch (_) {}
                }
                return {
                  ...p,
                  cheers_count: Number(realCheers),
                  reply_count: Number(realReplies),
                  reactors_preview: Array.isArray(preview) ? preview : [],
                };
              });

              setPosts(enriched);
            } else {
              // Fallback
              const enriched: PartnerPost[] = postsData.map((p) => ({
                ...p,
                cheers_count: p.cheers_count ?? 0,
                reply_count: p.reply_count ?? 0,
                reactors_preview: [],
              }));
              setPosts(enriched);
            }
          } else {
            setPosts([]);
          }
        } else {
          setPrograms([]);
          setPosts([]);
          setAnalytics([]);
          setApplications([]);
          setFollowers([]);
          setActivities([]);
          setMessages([]);
          setSubscription(null);
          setCampaign(null);
        }

        hasLoadedOnce.current = true;
      } catch (error) {
        console.error("Error in fetchData:", error);
        setProfile(null);
        setPrograms([]);
        setPosts([]);
        setAnalytics([]);
        setApplications([]);
        setActivities([]);
        setMessages([]);
        setSubscription(null);
        setCampaign(null);
      } finally {
        setLoading(false);
      }
    },
    [user?.id]
  );

  useEffect(() => {
    if (isAuthenticated) {
      void fetchData();
    } else {
      hasLoadedOnce.current = false;
      setLoading(false);
      setProfile(null);
      setPrograms([]);
      setPosts([]);
      setAnalytics([]);
      setApplications([]);
      setActivities([]);
      setMessages([]);
      setSubscription(null);
      setCampaign(null);
    }
  }, [isAuthenticated, fetchData]);

  // Live partnership inbox via Supabase Realtime (postgres_changes).
  useEffect(() => {
    if (!user?.id || !isAuthenticated) return;

    const applyRealtimeRow = (
      row: PartnershipMessage,
      event: "INSERT" | "UPDATE" | "DELETE"
    ) => {
      setMessages((prev) => {
        if (event === "INSERT") {
          if (prev.some((m) => m.id === row.id)) return prev;
          return [row, ...prev];
        }
        if (event === "UPDATE") {
          return prev.map((m) => (m.id === row.id ? { ...m, ...row } : m));
        }
        return prev.filter((m) => m.id !== row.id);
      });
    };

    const reloadMessages = async () => {
      try {
        const { data: messagesData, error: messagesError } = await supabase
          .from("partnership_messages")
          .select("*")
          .or(`partner_id.eq.${user.id},receiver_id.eq.${user.id},sender_id.eq.${user.id}`)
          .order("sent_at", { ascending: false })
          .limit(200);

        if (messagesError || !messagesData) return;
        setMessages(messagesData);
      } catch (error) {
        console.error("Realtime message reload error:", error);
      }
    };

    const channel = supabase
      .channel(`partnership_messages_partner_${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "partnership_messages",
          filter: `partner_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as PartnershipMessage;
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            if (row?.id) applyRealtimeRow(row, payload.eventType);
          } else {
            void reloadMessages();
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "partnership_messages",
          filter: `receiver_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as PartnershipMessage;
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            if (row?.id) applyRealtimeRow(row, payload.eventType);
          } else {
            void reloadMessages();
          }
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "partnership_messages",
          filter: `sender_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as PartnershipMessage;
          if (payload.eventType === "INSERT" || payload.eventType === "UPDATE") {
            if (row?.id) applyRealtimeRow(row, payload.eventType);
          } else {
            void reloadMessages();
          }
        }
      )
      .subscribe();

    return () => {
      void supabase.removeChannel(channel);
    };
  }, [user?.id, isAuthenticated]);

  // Realtime subscription for stories, story cheers & story replies to keep likes and comments 100% in sync live
  useEffect(() => {
    if (!isAuthenticated || !user?.id) return;

    let timeoutId: any = null;
    const debouncedReload = () => {
      if (timeoutId) clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        void fetchData({ silent: true });
      }, 300);
    };

    const channel = supabase
      .channel(`partner_posts_live_sync_${user.id}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "student_story_cheers",
        },
        () => {
          debouncedReload();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "student_story_replies",
        },
        () => {
          debouncedReload();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "student_story_reply_cheers",
        },
        () => {
          debouncedReload();
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "student_stories",
        },
        () => {
          debouncedReload();
        }
      )
      .subscribe();

    // Periodic poll every 8 seconds and on window focus for rock-solid live metrics
    const interval = setInterval(() => {
      void fetchData({ silent: true });
    }, 8000);

    const onFocus = () => {
      void fetchData({ silent: true });
    };
    window.addEventListener("focus", onFocus);

    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      clearInterval(interval);
      window.removeEventListener("focus", onFocus);
      void supabase.removeChannel(channel);
    };
  }, [user?.id, isAuthenticated, fetchData]);

  const markPartnershipThreadReadLocal = useCallback(
    (partnerId: string, studentId: string, subject?: string | null) => {
      setMessages((prev) =>
        prev.map((m) => {
          if (
            partnershipThreadMatches(m, partnerId, studentId, subject) &&
            m.receiver_id === user?.id
          ) {
            return { ...m, is_read: true };
          }
          return m;
        })
      );
    },
    [user?.id]
  );

  const deleteProgram = useCallback(async (programId: string) => {
    const { data, error } = await supabase.rpc("delete_partner_program", {
      p_program_id: programId,
    });
    if (!error) {
      setPrograms((prev) => prev.filter((p) => p.id !== programId));
      // Keep related local state in sync
      setApplications((prev) => prev.filter((a) => a.program_id !== programId));
      setAnalytics((prev) => prev.filter((a) => a.program_id !== programId));
    }
    return { data, error };
  }, []);

  // Toggle a program between "Live" and "Closed" without deleting it — unlike
  // deleteProgram (which hard-removes applications + messages), closing just
  // flips is_active so applicants keep their history in My Applications while
  // the listing drops off Discover for new applicants.
  const setProgramActive = useCallback(async (programId: string, isActive: boolean) => {
    const { error } = await supabase
      .from("programs")
      .update({ is_active: isActive })
      .eq("id", programId);
    if (!error) {
      setPrograms((prev) =>
        prev.map((p) => (p.id === programId ? { ...p, is_active: isActive } : p))
      );
    }
    return { error };
  }, []);

  const updateApplicationStatus = useCallback(
    async (applicationId: string, status: string, rejectionReason?: string) => {
      const { data, error } = await supabase.rpc("update_partner_application_status", {
        p_application_id: applicationId,
        p_status: status,
        p_rejection_reason: rejectionReason ?? null,
      });
      if (!error) {
        const next = status === "pending" ? "new" : status;
        setApplications((prev) =>
          prev.map((app) =>
            app.id === applicationId
              ? {
                  ...app,
                  status: next,
                  rejection_reason:
                    next === "rejected" ? rejectionReason || app.rejection_reason : null,
                }
              : app
          )
        );

        // Do NOT auto-sync a Google Calendar placeholder here. Partners pick
        // the interview slot via schedule_application_interview; Calendar sync
        // runs from that save path so we never overwrite a real datetime with
        // the old now+3days fallback.
      }
      return { data, error };
    },
    []
  );

  const deleteApplication = useCallback(async (applicationId: string) => {
    const { data, error } = await supabase.rpc("delete_partner_application", {
      p_application_id: applicationId,
    });
    if (!error) {
      setApplications((prev) => prev.filter((a) => a.id !== applicationId));
    }
    return { data, error };
  }, []);

  const deletePartnershipMessage = useCallback(async (messageId: string) => {
    const { data, error } = await supabase.rpc("delete_partnership_message", {
      p_message_id: messageId,
    });
    if (!error) {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? {
                ...m,
                deleted_at: new Date().toISOString(),
                deleted_by: user?.id || null,
                content: "",
                attachment_url: null,
                attachment_name: null,
                attachment_mime_type: null,
                attachment_size: null,
              }
            : m
        )
      );
    }
    return { data, error };
  }, [user?.id]);

  const deletePartnershipThread = useCallback(
    async (studentId: string, subject?: string | null) => {
      const { data, error } = await supabase.rpc("delete_partnership_thread", {
        p_student_id: studentId,
        p_subject: subject ?? null,
      });
      if (!error) {
        const norm = normalizePartnershipSubject(subject);
        setMessages((prev) =>
          prev.filter((m) => {
            const student =
              m.sender_id === user?.id ? m.receiver_id : m.sender_id;
            if (student !== studentId) return true;
            if (!subject) return false;
            return normalizePartnershipSubject(m.subject) !== norm;
          })
        );
      }
      return { data, error };
    },
    [user?.id]
  );

  const sendApplicationMessage = useCallback(
    async (opts: {
      studentId: string;
      applicationId: string;
      subject: string;
      content: string;
    }) => {
      if (!user?.id) return { error: new Error("Not signed in") };
      const { error } = await supabase.from("partnership_messages").insert({
        sender_id: user.id,
        receiver_id: opts.studentId,
        partner_id: user.id,
        application_id: opts.applicationId,
        subject: opts.subject,
        content: opts.content,
        message_type: "direct",
        is_from_system: false,
        is_read: false,
      });
      if (!error) {
        await fetchData({ silent: true });
      }
      return { error };
    },
    [user?.id, fetchData]
  );

  const sendFollowerMessage = useCallback(
    async (opts: { studentId: string; content: string; subject?: string }) => {
      if (!user?.id) return { error: new Error("Not signed in"), messageId: null };
      const { data, error } = await supabase.rpc("send_partner_follower_message", {
        p_student_id: opts.studentId,
        p_content: opts.content,
        p_subject: opts.subject ?? "Follower outreach",
      });
      if (!error) {
        await fetchData({ silent: true });
      }
      return {
        error: error ? new Error(error.message) : null,
        messageId: (data as string | null) ?? null,
      };
    },
    [user?.id, fetchData]
  );

  const updateProfile = useCallback(async () => {
    if (!user?.id) return;
    try {
      const { data: partnerData, error: partnerError } = await supabase
        .from("partners")
        .select("*")
        .eq("id", user.id)
        .maybeSingle();

      if (partnerError) {
        console.error("Error updating profile data:", partnerError);
      } else {
        setProfile(partnerData);
      }
    } catch (error) {
      console.error("Error in updateProfile:", error);
    }
  }, [user?.id]);

  const saveInstitutionName = useCallback(
    async (institutionName: string) => {
      if (!user?.id) {
        return { error: new Error("Not signed in") };
      }

      const trimmed = institutionName.trim();
      if (!trimmed) {
        return { error: new Error("Institution name is required") };
      }

      const email = (user.email || profile?.email || "").trim().toLowerCase();

      try {
        if (profile?.id) {
          const { error } = await supabase
            .from("partners")
            .update({ institution_name: trimmed })
            .eq("id", user.id);

          if (error) {
            return { error: new Error(error.message) };
          }

          setProfile((prev) =>
            prev ? { ...prev, institution_name: trimmed } : prev
          );
        } else {
          const { data, error } = await supabase
            .from("partners")
            .insert({
              id: user.id,
              institution_name: trimmed,
              email: email || undefined,
            })
            .select("*");

          if (error) {
            return { error: new Error(error.message) };
          }

          setProfile(data?.[0] ?? null);
          await fetchData({ silent: true });
        }

        return { error: null };
      } catch (error) {
        console.error("Error saving institution name:", error);
        return {
          error:
            error instanceof Error
              ? error
              : new Error("Failed to save institution name"),
        };
      }
    },
    [user?.id, user?.email, profile, fetchData]
  );

  const createPost = useCallback(
    async (payload: {
      title?: string;
      body: string;
      media_image_url?: string | null;
      tags?: string[];
      audience_type?: "highSchool" | "afterHighSchool" | "all";
      is_published?: boolean;
    }) => {
      if (!user?.id) throw new Error("Not signed in");
      
      const pro = isPartnerProPlus(subscription);
      if (!pro && posts.length >= FREE_TRIAL_POST_LIMIT) {
        throw new Error(
          `Free Trial includes up to ${FREE_TRIAL_POST_LIMIT} posts (5 listings total: 3 programs & 2 posts). Upgrade to Pro+ for unlimited posts.`
        );
      }

      const orgName = profile?.institution_name || "Institution Partner";
      const newRecord = {
        author_user_id: user.id,
        partner_id: user.id,
        name: orgName,
        title: payload.title?.trim() || null,
        body: payload.body.trim(),
        media_image_url: payload.media_image_url || null,
        tags: payload.tags || [],
        audience_type: payload.audience_type || "all",
        is_verified_badge: Boolean(profile?.is_verified),
        is_published: payload.is_published ?? true,
        is_approved: true,
        moderation_status: "approved",
        cheers_count: 0,
        reply_count: 0,
      };
      const { data, error } = await supabase
        .from("student_stories")
        .insert([newRecord])
        .select()
        .single();
      if (error) throw error;
      await fetchData({ silent: true });
      return data as PartnerPost;
    },
    [user?.id, subscription, posts.length, profile?.institution_name, profile?.is_verified, fetchData]
  );

  const updatePost = useCallback(
    async (
      id: string,
      payload: {
        title?: string;
        body?: string;
        media_image_url?: string | null;
        tags?: string[];
        audience_type?: "highSchool" | "afterHighSchool" | "all";
        is_published?: boolean;
      }
    ) => {
      if (!user?.id) throw new Error("Not signed in");
      const { data, error } = await supabase
        .from("student_stories")
        .update({
          ...payload,
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .or(`partner_id.eq.${user.id},author_user_id.eq.${user.id}`)
        .select()
        .single();
      if (error) throw error;
      await fetchData({ silent: true });
      return data as PartnerPost;
    },
    [user?.id, fetchData]
  );

  const deletePost = useCallback(
    async (id: string) => {
      if (!user?.id) throw new Error("Not signed in");
      const { error } = await supabase
        .from("student_stories")
        .delete()
        .eq("id", id)
        .or(`partner_id.eq.${user.id},author_user_id.eq.${user.id}`);
      if (error) throw error;
      await fetchData({ silent: true });
    },
    [user?.id, fetchData]
  );

  const togglePostPublish = useCallback(
    async (id: string, isPublished: boolean) => {
      if (!user?.id) throw new Error("Not signed in");
      const { error } = await supabase
        .from("student_stories")
        .update({
          is_published: isPublished,
          is_approved: isPublished,
          moderation_status: isPublished ? "approved" : "draft",
          updated_at: new Date().toISOString(),
        })
        .eq("id", id)
        .or(`partner_id.eq.${user.id},author_user_id.eq.${user.id}`);
      if (error) throw error;
      await fetchData({ silent: true });
    },
    [user?.id, fetchData]
  );

  const fetchPostReactors = useCallback(async (storyId: string): Promise<PostReactor[]> => {
    try {
      try {
        const { data, error } = await supabase.rpc("list_story_cheers", {
          p_story_id: storyId,
        });
        if (!error && data) {
          let list = data;
          if (typeof list === "string") {
            try { list = JSON.parse(list); } catch (_) {}
          }
          if (Array.isArray(list) && list.length > 0) {
            return list as PostReactor[];
          }
        }
      } catch (rpcErr) {
        console.warn("RPC list_story_cheers failed, falling back to direct table query:", rpcErr);
      }

      // Direct table fallback
      const { data: rows, error: selectErr } = await supabase
        .from("student_story_cheers")
        .select("user_id, created_at")
        .eq("story_id", storyId)
        .order("created_at", { ascending: false });

      if (selectErr || !rows || rows.length === 0) {
        return [];
      }

      const userIds = Array.from(new Set(rows.map((r: any) => r.user_id).filter(Boolean)));
      const [usersRes, partnersRes, profilesRes] = await Promise.all([
        supabase.from("users").select("id, full_name, first_name, last_name, profile_image_url").in("id", userIds),
        supabase.from("partners").select("id, institution_name, logo_url, avatar_url").in("id", userIds),
        supabase.from("student_public_profiles").select("id, display_name, profile_image_url").in("id", userIds),
      ]);

      const userMap = new Map((usersRes.data || []).map((u: any) => [u.id, u]));
      const partnerMap = new Map((partnersRes.data || []).map((p: any) => [p.id, p]));
      const profileMap = new Map((profilesRes.data || []).map((p: any) => [p.id, p]));

      return rows.map((r: any) => {
        const partner = partnerMap.get(r.user_id);
        const profile = profileMap.get(r.user_id);
        const usr = userMap.get(r.user_id);

        let displayName = "Learner";
        let profileImageUrl: string | null = null;

        if (partner) {
          displayName = partner.institution_name || "Institution";
          profileImageUrl = partner.logo_url || partner.avatar_url || null;
        } else if (profile) {
          displayName = profile.display_name || "Learner";
          profileImageUrl = profile.profile_image_url || null;
        } else if (usr) {
          displayName = [usr.first_name, usr.last_name].filter(Boolean).join(" ") || usr.full_name || "Learner";
          profileImageUrl = usr.profile_image_url || null;
        }

        return {
          user_id: r.user_id,
          display_name: displayName,
          profile_image_url: profileImageUrl,
          created_at: r.created_at,
        };
      });
    } catch (e) {
      console.error("Error fetching post reactors:", e);
      return [];
    }
  }, []);

  const fetchPostReplies = useCallback(async (storyId: string): Promise<PostReply[]> => {
    try {
      try {
        const { data, error } = await supabase.rpc("list_story_replies", {
          p_story_id: storyId,
        });
        if (!error && data) {
          let list = data;
          if (typeof list === "string") {
            try { list = JSON.parse(list); } catch (_) {}
          }
          if (Array.isArray(list) && list.length > 0) {
            return list as PostReply[];
          }
        }
      } catch (rpcErr) {
        console.warn("RPC list_story_replies failed, falling back to direct table query:", rpcErr);
      }

      // Direct table fallback
      const { data: rows, error: selectErr } = await supabase
        .from("student_story_replies")
        .select("id, story_id, author_user_id, body, created_at, parent_reply_id, cheers_count")
        .eq("story_id", storyId)
        .order("created_at", { ascending: true });

      if (selectErr || !rows || rows.length === 0) {
        return [];
      }

      const authorIds = Array.from(new Set(rows.map((r: any) => r.author_user_id).filter(Boolean)));
      const replyIds = rows.map((r: any) => r.id);

      const [usersRes, partnersRes, profilesRes, myCheersRes] = await Promise.all([
        supabase.from("users").select("id, full_name, first_name, last_name, profile_image_url, grade").in("id", authorIds),
        supabase.from("partners").select("id, institution_name, logo_url, avatar_url").in("id", authorIds),
        supabase.from("student_public_profiles").select("id, display_name, profile_image_url, grade").in("id", authorIds),
        user?.id
          ? supabase.from("student_story_reply_cheers").select("reply_id").eq("user_id", user.id).in("reply_id", replyIds)
          : Promise.resolve({ data: [] }),
      ]);

      const userMap = new Map((usersRes.data || []).map((u: any) => [u.id, u]));
      const partnerMap = new Map((partnersRes.data || []).map((p: any) => [p.id, p]));
      const profileMap = new Map((profilesRes.data || []).map((p: any) => [p.id, p]));
      const cheeredReplySet = new Set(((myCheersRes as any)?.data || []).map((c: any) => c.reply_id));

      const enriched: PostReply[] = rows.map((r: any) => {
        const partner = partnerMap.get(r.author_user_id);
        const profile = profileMap.get(r.author_user_id);
        const usr = userMap.get(r.author_user_id);

        let displayName = "Learner";
        let profileImageUrl: string | null = null;
        let grade: string | null = null;
        let isPartner = false;

        if (partner) {
          isPartner = true;
          displayName = partner.institution_name || "Institution";
          profileImageUrl = partner.logo_url || partner.avatar_url || null;
        } else if (profile) {
          displayName = profile.display_name || "Learner";
          profileImageUrl = profile.profile_image_url || null;
          grade = profile.grade || null;
        } else if (usr) {
          displayName = [usr.first_name, usr.last_name].filter(Boolean).join(" ") || usr.full_name || "Learner";
          profileImageUrl = usr.profile_image_url || null;
          grade = usr.grade || null;
        }

        return {
          id: r.id,
          story_id: r.story_id,
          author_user_id: r.author_user_id,
          body: r.body,
          created_at: r.created_at,
          parent_reply_id: r.parent_reply_id || null,
          display_name: displayName,
          profile_image_url: profileImageUrl,
          grade: grade,
          is_partner: isPartner,
          cheers_count: typeof r.cheers_count === "number" ? r.cheers_count : 0,
          has_cheered: cheeredReplySet.has(r.id),
        };
      });

      // If any reply has no parent_reply_id but mentions @Name, attempt to match it to that parent
      for (const item of enriched) {
        if (!item.parent_reply_id && item.body && item.body.startsWith("@")) {
          const match = item.body.match(/^@([a-zA-Z0-9_\s]+?)(?:,|\s|$)/);
          if (match && match[1]) {
            const targetName = match[1].trim().toLowerCase();
            const parent = enriched.find(
              (p) =>
                p.id !== item.id &&
                !p.parent_reply_id &&
                p.display_name.toLowerCase().includes(targetName)
            );
            if (parent) {
              item.parent_reply_id = parent.id;
            }
          }
        }
      }

      return enriched;
    } catch (e) {
      console.error("Error fetching post replies:", e);
      return [];
    }
  }, [user?.id]);

  const addPostReply = useCallback(
    async (storyId: string, body: string, parentReplyId?: string) => {
      if (!user?.id) throw new Error("Not signed in");
      const cleanBody = body.trim();
      if (!cleanBody) return;

      // Ensure partner user exists in public.users to satisfy FK constraints
      try {
        const { data: existingUser } = await supabase
          .from("users")
          .select("id")
          .eq("id", user.id)
          .maybeSingle();

        if (!existingUser) {
          await supabase.from("users").upsert({
            id: user.id,
            email: user.email || `${user.id}@partner.skutopia.com`,
            full_name: profile?.institution_name || "Institution Partner",
            first_name: profile?.institution_name || "Partner",
          }, { onConflict: "id" });
        }
      } catch (e) {
        console.warn("User existence check for reply warning:", e);
      }

      // Try RPC first for security definer handling and instant enriched object
      try {
        const { data, error } = await supabase.rpc("add_story_reply", {
          p_story_id: storyId,
          p_body: cleanBody,
          p_parent_reply_id: parentReplyId || null,
        });
        if (!error && data) {
          await fetchData({ silent: true });
          return data as PostReply;
        }
      } catch (rpcErr) {
        console.warn("add_story_reply RPC failed, falling back to direct insert:", rpcErr);
      }

      const { error } = await supabase.from("student_story_replies").insert({
        story_id: storyId,
        author_user_id: user.id,
        body: cleanBody,
        parent_reply_id: parentReplyId || null,
      });
      if (error) throw error;
      await fetchData({ silent: true });
    },
    [user?.id, user?.email, profile?.institution_name, fetchData]
  );

  const deletePostReply = useCallback(
    async (replyId: string) => {
      try {
        const { error } = await supabase.rpc("delete_story_reply", {
          p_reply_id: replyId,
        });
        if (!error) {
          await fetchData({ silent: true });
          return;
        }
      } catch (rpcErr) {
        console.warn("delete_story_reply RPC failed, falling back to direct delete:", rpcErr);
      }

      const { error } = await supabase
        .from("student_story_replies")
        .delete()
        .eq("id", replyId);
      if (error) throw error;
      await fetchData({ silent: true });
    },
    [fetchData]
  );

  const toggleReplyCheer = useCallback(
    async (replyId: string) => {
      if (!user?.id) throw new Error("Not signed in");

      try {
        const { data: existingUser } = await supabase
          .from("users")
          .select("id")
          .eq("id", user.id)
          .maybeSingle();

        if (!existingUser) {
          await supabase.from("users").upsert(
            {
              id: user.id,
              email: user.email || `${user.id}@partner.skutopia.com`,
              full_name: profile?.institution_name || "Institution Partner",
              first_name: profile?.institution_name || "Partner",
            },
            { onConflict: "id" }
          );
        }
      } catch (e) {
        console.warn("User existence check for reply cheer warning:", e);
      }

      const { data: existing } = await supabase
        .from("student_story_reply_cheers")
        .select("reply_id")
        .eq("reply_id", replyId)
        .eq("user_id", user.id)
        .maybeSingle();

      if (existing) {
        const { error } = await supabase
          .from("student_story_reply_cheers")
          .delete()
          .eq("reply_id", replyId)
          .eq("user_id", user.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("student_story_reply_cheers").insert({
          reply_id: replyId,
          user_id: user.id,
        });
        if (error) throw error;
      }
    },
    [user?.id, user?.email, profile?.institution_name]
  );

  const activeCount = useMemo(
    () => programs.filter((p) => p.is_active).length,
    [programs]
  );

  const isProPlus = useMemo(() => isPartnerProPlus(subscription), [subscription]);

  const maxProgramListings = useMemo(() => {
    const fromPlan = subscription?.plan?.max_campaigns;
    if (typeof fromPlan === "number" && fromPlan > 0) return fromPlan;
    return isProPlus ? 100 : FREE_TRIAL_PROGRAM_LIMIT;
  }, [subscription?.plan?.max_campaigns, isProPlus]);

  const maxPostListings = useMemo(() => {
    return isProPlus ? 100 : FREE_TRIAL_POST_LIMIT;
  }, [isProPlus]);

  const totalListingCount = useMemo(() => {
    return programs.length + posts.length;
  }, [programs.length, posts.length]);

  const maxTotalListings = useMemo(() => {
    return isProPlus ? 200 : FREE_TRIAL_TOTAL_LIMIT;
  }, [isProPlus]);

  const value = useMemo<PartnerDataValue>(
    () => ({
      profile,
      programs,
      posts,
      analytics,
      applications,
      followers,
      activities,
      messages,
      chatPeers,
      subscription,
      campaign,
      subscriptionPlans,
      platformLearnerCount,
      loading,
      activeCount,
      isProPlus,
      maxProgramListings,
      maxPostListings,
      totalListingCount,
      maxTotalListings,
      refresh: () => fetchData({ silent: true }),
      markPartnershipThreadReadLocal,
      deleteProgram,
      setProgramActive,
      createPost,
      updatePost,
      togglePostPublish,
      deletePost,
      fetchPostReactors,
      fetchPostReplies,
      addPostReply,
      deletePostReply,
      toggleReplyCheer,
      updateApplicationStatus,
      deleteApplication,
      deletePartnershipMessage,
      deletePartnershipThread,
      sendApplicationMessage,
      sendFollowerMessage,
      updateProfile,
      saveInstitutionName,
      user,
      isAuthenticated,
    }),
    [
      profile,
      programs,
      posts,
      analytics,
      applications,
      followers,
      activities,
      messages,
      chatPeers,
      subscription,
      campaign,
      subscriptionPlans,
      platformLearnerCount,
      loading,
      activeCount,
      isProPlus,
      maxProgramListings,
      maxPostListings,
      totalListingCount,
      maxTotalListings,
      fetchData,
      markPartnershipThreadReadLocal,
      deleteProgram,
      setProgramActive,
      createPost,
      updatePost,
      togglePostPublish,
      deletePost,
      fetchPostReactors,
      fetchPostReplies,
      addPostReply,
      deletePostReply,
      toggleReplyCheer,
      updateApplicationStatus,
      deleteApplication,
      deletePartnershipMessage,
      deletePartnershipThread,
      sendApplicationMessage,
      sendFollowerMessage,
      updateProfile,
      saveInstitutionName,
      user,
      isAuthenticated,
    ]
  );

  return createElement(
    PartnerDataContext.Provider,
    { value },
    children
  );
}

export function usePartnerData() {
  const ctx = useContext(PartnerDataContext);
  if (!ctx) {
    throw new Error("usePartnerData must be used within PartnerDataProvider");
  }
  return ctx;
}
